<template>
  <div class="relative z-[100]">
    <Overlay :visible="isOpen" @click="close" />
    <Drawer
      ref="activeOffersSidebarRef"
      class="active-offers-sidebar__aside"
      :is-open="isOpen"
      @click:close="close"
    >
      <template #header>
        <div class="px-4 py-4 border-b border-gray-200">
          <div class="text-lg font-semibold text-gray-900">
            {{ t('general.yourRequests') }}
          </div>
        </div>
      </template>
      <div v-if="isOpen" class="flex flex-col flex-1 overflow-y-auto px-4">
        <div
          v-if="activeOffers.length === 0 && activeDateRequests.length === 0"
          class="px-4 py-8 text-center"
        >
          <p class="text-gray-500">{{ t('general.noActiveOffers') }}</p>
        </div>
        <div v-else>
          <div class="mb-4 text-base font-medium text-black">
            {{ t('general.hereAreSomeClassesYouMightLike') }}
          </div>
          <div class="space-y-2">
            <!-- Offers -->
            <div
              v-for="offer in activeOffers"
              :key="`offer-${offer.id}`"
              class="py-2"
            >
              <div class="flex gap-3">
                <!-- Image -->
                <div class="flex-shrink-0 relative">
                  <img
                    v-if="offer.image"
                    :src="offer.image"
                    :alt="offer.title || ''"
                    class="w-40 min-h-32 h-32 object-cover rounded-lg"
                    loading="lazy"
                  />
                  <!-- Badge -->
                  <div
                    class="absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-thin bg-lime-50 text-teal-900"
                  >
                    {{ t('general.responseAvailable') }}
                  </div>
                </div>
                <!-- Content -->
                <div class="flex-1 flex flex-col min-w-0">
                  <!-- Title -->
                  <div
                    v-if="offer?.title"
                    class="text-xs font-medium text-black mb-1 line-clamp-2"
                  >
                    {{ offer.title }}
                  </div>

                  <!-- Date, People, Location -->
                  <div
                    class="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-0.5 sm:gap-2 mb-0.5 text-caption text-gray-500"
                  >
                    <span v-if="offer.firstDate" class="flex items-start gap-1">
                      <IconDuration class="w-3 h-3 flex-shrink-0 mt-1" />
                      <span>
                        {{ formatDate(offer.firstDate) }}
                        <span v-if="offer.totalDates && offer.totalDates > 1">
                          + {{ offer.totalDates - 1 }}
                          {{ t('general.moreDates') }}
                        </span>
                      </span>
                    </span>
                    <span
                      v-if="offer.requestedTickets"
                      class="flex items-start gap-1"
                    >
                      <IconPeople class="w-3 h-3 flex-shrink-0 mt-1" />
                      <span>
                        {{ offer.requestedTickets }}
                        {{
                          offer.requestedTickets === 1
                            ? t('general.perPerson')
                            : t('general.people')
                        }}
                      </span>
                    </span>
                    <span v-if="offer.location" class="flex items-start gap-1">
                      <IconLocation class="w-3 h-3 flex-shrink-0 mt-1" />
                      {{ offer.location }}
                    </span>
                  </div>

                  <!-- Price -->
                  <div v-if="offer.price" class="mt-1 font-semibold text-sm">
                    {{ formatPrice(offer.price, offer.priceCurrency) }}
                  </div>

                  <!-- Button -->
                  <div class="mt-2">
                    <Button
                      color="primary"
                      variant="contained"
                      :link="getOfferUrl(offer)"
                      class="py-1 text-sm font-semibold"
                      @click="close"
                    >
                      {{ t('general.buyNow') }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Date Requests -->
            <div
              v-for="dateRequest in activeDateRequests"
              :key="`dateRequest-${dateRequest.id}`"
              class="py-2"
            >
              <div class="flex gap-3">
                <!-- Image -->
                <div class="flex-shrink-0 relative">
                  <img
                    v-if="dateRequest.image"
                    :src="dateRequest.image"
                    :alt="dateRequest.title || ''"
                    class="w-40 min-h-32 h-32 object-cover rounded-lg"
                    loading="lazy"
                  />
                  <!-- Badge -->
                  <div
                    class="absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-thin bg-orange-50 text-orange-600"
                  >
                    {{ t('general.requestSent') }}
                  </div>
                </div>

                <!-- Content -->
                <div class="flex-1 flex flex-col min-w-0">
                  <!-- Title -->
                  <div
                    v-if="dateRequest.title"
                    class="text-xs font-medium text-black mb-1 line-clamp-2"
                  >
                    {{ dateRequest.title }}
                  </div>

                  <!-- Date, People, Location -->
                  <div
                    class="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-0.5 sm:gap-2 mb-0.5 text-caption text-gray-500"
                  >
                    <span
                      v-if="dateRequest.dates?.length"
                      class="flex items-start gap-1"
                    >
                      <IconDuration class="w-3 h-3 flex-shrink-0 mt-1" />
                      <span>
                        {{
                          dateRequest.dates[0]
                            ? formatDate(dateRequest.dates[0])
                            : ''
                        }}
                        <span v-if="dateRequest.dates.length > 1">
                          + {{ dateRequest.dates.length - 1 }}
                          {{ t('general.moreDates') }}
                        </span>
                      </span>
                    </span>
                    <span
                      v-if="dateRequest.numberOfTickets"
                      class="flex items-start gap-1"
                    >
                      <IconPeople class="w-3 h-3 flex-shrink-0 mt-1" />
                      <span>
                        {{ dateRequest.numberOfTickets }}
                        {{
                          dateRequest.numberOfTickets === 1
                            ? t('general.perPerson')
                            : t('general.people')
                        }}
                      </span>
                    </span>
                    <span
                      v-if="dateRequest.location"
                      class="flex items-start gap-1"
                    >
                      <IconLocation class="w-3 h-3 flex-shrink-0 mt-1" />
                      {{ dateRequest.location }}
                    </span>
                  </div>

                  <!-- Button -->
                  <div class="mt-auto">
                    <Button
                      color="primary"
                      variant="outlined"
                      :link="dateRequest?.url"
                      class="px-4 py-1 text-sm font-semibold bg-white"
                      @click="close"
                    >
                      {{ t('general.previewEvent') }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import { useOfferTracking } from '~/composables/useOfferTracking'
import { useDateRequestTracking } from '~/composables/useDateRequestTracking'
import { useActiveOffersSidebarUiState } from '~/composables/useUiState'
import IconDuration from '~/assets/offer/offer-icon-duration.svg?component'
import IconLocation from '~/assets/offer/offer-icon-location.svg?component'
import IconPeople from '~/assets/offer/offer-icon-people.svg?component'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { isOpen: isOpenRef, close } = useActiveOffersSidebarUiState()
const { activeOffers, removeOfferPage } = useOfferTracking()
const { activeDateRequests, removeDateRequestPage } = useDateRequestTracking()

// Ensure isOpen is always a ref for watch
const isOpen = isRef(isOpenRef) ? isOpenRef : ref(isOpenRef)

// Check and remove expired offers based on lastDate
const removeExpiredOffers = () => {
  const now = Date.now()
  const offers = [...activeOffers.value] // Copy array to avoid mutation during iteration

  offers.forEach((offer) => {
    if (offer.lastDate) {
      const lastDateTimestamp = new Date(offer.lastDate).getTime()
      // Check if the last available date has passed
      if (lastDateTimestamp < now) {
        removeOfferPage(offer.id)
      }
    }
  })
}

// Check and remove expired date requests based on dates
const removeExpiredDateRequests = () => {
  const now = Date.now()
  const dateRequests = [...activeDateRequests.value] // Copy array to avoid mutation during iteration

  dateRequests.forEach((dateRequest) => {
    if (dateRequest.dates && dateRequest.dates.length > 0) {
      // Check if all requested dates have passed
      const allDatesPassed = dateRequest.dates.every((date) => {
        const dateTimestamp = new Date(date).getTime()
        return dateTimestamp < now
      })
      if (allDatesPassed) {
        removeDateRequestPage(dateRequest.id)
      }
    }
  })
}

const getOfferUrl = (offer: { id: string; signature?: string }) => {
  const baseUrl = localePath(`/offer/${offer.id}`)
  if (offer.signature) {
    return `${baseUrl}?signature=${offer.signature}`
  }
  return baseUrl
}

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  return dayjs(dateString).format('dd, DD.MM.YY')
}

const formatPrice = (price: number, currency?: string) => {
  return formatPriceAmount(price, locale.value, currency || 'EUR')
}

// Watch for sidebar open to check expired offers and date requests
watch(isOpen, (newValue) => {
  if (newValue) {
    // When sidebar opens, check and remove expired offers and date requests
    removeExpiredOffers()
    removeExpiredDateRequests()
  }
})
</script>

<style lang="postcss" scoped>
.active-offers-sidebar__aside {
  @apply w-full sm:w-[600px] px-0 pt-0 pb-0 flex flex-col h-[100dvh];
}
</style>
